import { ArrowUpRight } from 'lucide-react'
import { useRef } from 'react'
import { useI18n } from '../i18n.jsx'
import { CarouselIndicators, useMobileCarousel } from './MobileCarousel.jsx'
import { useMobileCardHeight } from './useMobileCardHeight.js'
import './solutions.css'

function RoomShell() {
  return <g className="solution-structure">
    <path d="M40 200V65h340v135" />
    <path d="M29 211h362M40 65l12-12h316l12 12" />
    <rect x="245" y="86" width="103" height="77" rx="2" className="solution-glass" />
    <path d="M296.5 86v77M245 124.5h103" />
    <path d="M69 185h125M74 185v15m115-15v15" />
  </g>
}

function HydronicIllustration() {
  return <svg className="solution-illustration" viewBox="0 0 420 270" fill="none" aria-hidden="true" data-art-style="original-schematic">
    <RoomShell />
    <g className="solution-structure">
      <rect x="69" y="117" width="122" height="58" rx="5" className="solution-equipment" />
      <path d="M81 124v44m15-44v44m15-44v44m15-44v44m15-44v44m15-44v44m15-44v44m10-44v44" />
      <path d="M191 150h13m-13 13h13" />
      <rect x="204" y="143" width="20" height="28" rx="4" className="solution-control" />
      <path d="M210 150h8M210 155h8" />
      <rect x="285" y="214" width="64" height="42" rx="3" className="solution-equipment" />
      <path d="M298 225h16m-16 7h16m15-7v16" />
    </g>
    <g className="solution-active">
      <path d="M215 172v59c0 4 3 7 7 7h63M79 175v70c0 4 3 7 7 7h199" />
      <path d="m243 234 6 4-6 4m-120 6-6 4 6 4" />
      <circle cx="214" cy="161" r="3" className="solution-dot" />
    </g>
    <g className="solution-secondary">
      <path d="M104 103c-7-9 7-10 0-19m24 19c-7-9 7-10 0-19m24 19c-7-9 7-10 0-19" />
    </g>
  </svg>
}

function ElectricIllustration() {
  return <svg className="solution-illustration" viewBox="0 0 420 270" fill="none" aria-hidden="true" data-art-style="original-schematic">
    <RoomShell />
    <g className="solution-structure">
      <rect x="77" y="92" width="44" height="55" rx="4" className="solution-equipment" />
      <rect x="85" y="101" width="28" height="21" rx="1" className="solution-glass" />
      <path d="M86 134h7m11 0h7" />
      <path d="M40 211v45h340v-45" />
      <path d="M40 224h340" className="solution-secondary" />
    </g>
    <g className="solution-active">
      <path d="M86 239h240c13 0 13 10 0 10H88c-13 0-13-10 0-10" />
      <path d="M112 249v-10m29 10v-10m29 10v-10m29 10v-10m29 10v-10m29 10v-10m29 10v-10" />
      <path d="M99 148v58h-13v33" className="solution-signal" />
      <path d="m149 194 0-26m-5 6 5-6 5 6m52 20v-26m-5 6 5-6 5 6m52 20v-26m-5 6 5-6 5 6" />
      <path d="m99 106-4 7h7l-4 7" />
    </g>
  </svg>
}

function GardenIllustration() {
  return <svg className="solution-illustration" viewBox="0 0 420 270" fill="none" aria-hidden="true" data-art-style="original-schematic">
    <g className="solution-structure">
      <path d="M30 225h362M48 225V79h33v146M48 83h33" />
      <path d="M81 118h35v14m-19-14v-12m-10 0h20" />
      <path d="M81 122h26v10" />
      <rect x="101" y="132" width="37" height="52" rx="5" className="solution-equipment" />
      <rect x="109" y="141" width="21" height="15" rx="2" className="solution-glass" />
      <circle cx="120" cy="170" r="5" className="solution-control" />
      <g data-plant="sword-leaf">
        <g className="solution-leaves">
          <path d="M201 225 191 193c-17-14-23-30-22-47 12 14 20 28 26 47Z" />
          <path d="M201 225 195 193c-15-24-18-43-15-66 11 20 17 40 20 66Z" />
          <path d="M201 225 198 193c-6-31-3-51 10-68 1 24-1 46-5 68Z" />
          <path d="M201 225 203 193c3-27 9-43 18-56 3 25-4 44-13 56Z" />
          <path d="M201 225 209 193c10-12 16-24 24-43 2 20-5 33-19 43Z" />
        </g>
      </g>
      <g data-plant="small-leaf">
        <path d="M253 225v-28m0 16-9-8m9 1 8-8" />
        <g className="solution-leaves">
          <path d="M253 199c-7-5-7-14 0-20 7 6 7 15 0 20Z" />
          <path d="M245 209c-8-1-11-8-7-15 8 2 11 8 7 15Z" />
          <path d="M260 202c-2-8 1-13 8-15 2 8-1 13-8 15Z" />
        </g>
      </g>
      <g data-plant="monstera">
        <path d="M316 225v-31q-1-21-13-31m13 31q4-37 17-49m-16 49q6-11 19-17" />
        <g className="solution-leaves">
          <path d="M331 154c-13-1-18-9-14-19 3-6 8-10 15-10l-5 9q0 3 3 1l7-9q9 0 15 8l-11 3q-3 2 1 3l13-2q5 5 6 12l-12-3q-3 0-1 3l12 7c-12 5-22 3-29-3Z" />
          <path d="M306 172c-10 5-21 6-32 2l8-9q2-3-2-2l-9 5q1-8 6-13l9 1q3 0 1-2l-5-5q7-7 15-6l3 8q1 3 3 0l1-7c12 3 15 16 2 28Z" />
          <path d="M333 185c-8-3-10-12-3-19l5 6q2 2 2-1l-1-7q9-2 15 4l-6 4q-2 2 1 2l9-1q5 5 6 13l-8-4q-3-1-2 2l4 6q-13 5-22-5Z" />
        </g>
      </g>
    </g>
    <g className="solution-active">
      <path d="M119 185v57c0 6 4 10 10 10h245" />
      {Array.from({ length: 9 }, (_, index) => <path
        key={index}
        className="solution-water"
        d="M-4 0c0-4 4-8 4-8s4 4 4 8a4 4 0 0 1-8 0Z"
        transform={`translate(${169 + 24 * index} 239)`}
      />)}
      <path d="m152 248 6 4-6 4" />
    </g>
    <g className="solution-secondary">
      <circle cx="360" cy="82" r="14" />
      <path d="M360 57v-6m0 62v-6m-25-25h-6m62 0h-6m-43-18-4-4m44 44-4-4m0-36 4-4m-44 44 4-4" />
    </g>
  </svg>
}

const solutionTypes = [
  { id: 'hydronic', illustration: HydronicIllustration, families: ['valve', 'panel'] },
  { id: 'electric', illustration: ElectricIllustration, families: ['sensor', 'control', 'plug'] },
  { id: 'garden', illustration: GardenIllustration, families: ['water'] },
]

export default function Solutions({ mailto }) {
  const { t, language } = useI18n()
  const track = useRef(null)
  const carousel = useMobileCarousel(track)
  useMobileCardHeight(track, language, .8, 'h3, .solution-description', '.solution-illustration')
  return <section id="products" className="section products-section solutions-section uniform-section"><div className="section-inner">
    <div className="section-heading"><div><h2>{t('site.solutions_title')}</h2></div><p>{t('site.solutions_description')}</p></div>
    <div className="solutions-grid mobile-carousel-track" ref={track}>{solutionTypes.map(({ id, illustration: Illustration, families }) => {
      const title = t(`site.solution_${id}_title`)
      return <article id={`solution-${id}`} className="solution-card" data-solution={id} key={id}>
        {families.map((family) => <span className="solution-anchor" id={`product-${family}`} key={family} aria-hidden="true" />)}
        <h3><a className="solution-enquire" href={mailto(t('site.email_subject_technical'), title)} title={title}>
          <span>{title}</span><ArrowUpRight size={32} aria-hidden="true" />
        </a></h3>
        <Illustration />
        <p className="solution-description">{t(`site.solution_${id}_description`)}</p>
      </article>
    })}</div>
    <CarouselIndicators carousel={carousel} title={t('site.solutions_title')} labels={solutionTypes.map(({ id }) => t(`site.solution_${id}_title`))} />
  </div></section>
}
